import isNil from 'lodash-es/isNil';
import { useClient, useItemContext, useQuery } from '../..';
import { isMetricWithHorizon } from '../../enums/MetricIds';
import TimeSteps from '../../enums/TimeSteps';
import { useConvertedValue } from '../../hooks';
import { AggregationType } from '../../resources/measurements.type';
import { formatHistoryDataForCharts, getChartDomains, getColorGradientLimits } from '../charts';
import { denormalizeDataPoint, normalizeDataPoint } from '../charts/charts';
import { getMetricsToFetch, getPeriodByPeriodId } from '../history';
const timeStep = TimeSteps.perHours;
const useMetricSummaryChart = ({ metric, prefetch }) => {
    const { kind, id, timezone } = useItemContext();
    const period = getPeriodByPeriodId({ periodId: 'current_day', timezone });
    const client = useClient();
    const { convertNumberValue, getUnit } = useConvertedValue();
    const unit = getUnit(metric);
    const metricsToFetch = getMetricsToFetch([metric]);
    const plotMeasurementsRequest = useQuery(client.measurements.plots.histories.get1_1({
        plotId: id,
        ...period,
        span: timeStep,
        metrics: metricsToFetch
    }), {
        enabled: kind === 'plot' && !isNil(period) && period.before !== period.since && isNil(prefetch)
    });
    const deviceMeasurementsRequest = useQuery(client.measurements.devices.histories.get1_1({
        deviceId: id,
        ...period,
        span: timeStep,
        metrics: metricsToFetch
    }), {
        enabled: (kind === 'device' || kind === 'station') &&
            !isNil(period) &&
            period.before !== period.since &&
            isNil(prefetch)
    });
    const isLoading = prefetch?.isLoading || plotMeasurementsRequest.isLoading || deviceMeasurementsRequest.isLoading;
    const measurements = prefetch?.measurements || plotMeasurementsRequest.data || deviceMeasurementsRequest.data;
    const data = measurements && !isMetricWithHorizon(metric)
        ? formatHistoryDataForCharts({
            data: measurements,
            metric,
            conversionFct: (value) => convertNumberValue({ metric, value })
        })
        : [];
    const windDirectionData = measurements && metricsToFetch.includes('DD')
        ? formatHistoryDataForCharts({
            data: measurements,
            metric: 'DD',
            aggregateToExtract: AggregationType.circular_avg,
            conversionFct: (value) => convertNumberValue({ metric: 'DD', value })
        })
        : [];
    const { yDomain, absoluteYDomain, xDomain } = getChartDomains({ metric, timeStep, unit, data });
    const normalizeScale = normalizeDataPoint(yDomain);
    const normalizedData = data.map((point) => ({
        ...point,
        y: normalizeScale(point.value)
    }));
    const denormalizeFct = denormalizeDataPoint(yDomain);
    const getGradient = () => getColorGradientLimits({ yDomain, absoluteYDomain });
    return {
        absoluteYDomain,
        data: normalizedData,
        denormalizeFct,
        getGradient,
        isLoading,
        period,
        windDirectionData,
        xDomain,
        yDomain
    };
};
export default useMetricSummaryChart;
